import { logEvent } from "firebase/analytics"
import { analytics } from "lib/firebase"

// Global gtag function (loaded from Google Analytics script)
const gtag =
  window.gtag ||
  function () {
  }

/**
 * Track page views
 * @param {string} page_title - The title of the page
 * @param {string} page_location - The URL of the page
 */
export const trackPageView = (page_title, page_location) => {
  try {
    // Google Analytics 4 via gtag
    gtag("config", "G-Z3C1EBWVDS", {
      page_title: page_title,
      page_location: page_location,
    })

    // Firebase Analytics
    logEvent(analytics, "page_view", {
      page_title: page_title,
      page_location: page_location,
    })

  } catch (error) {
  }
}

/**
 * Track custom events
 * @param {string} action - The action being tracked
 * @param {object} parameters - Additional parameters for the event
 */
export const trackEvent = (action, parameters = {}) => {
  try {
    // Google Analytics 4 via gtag
    gtag("event", action, parameters)

    // Firebase Analytics
    logEvent(analytics, action, parameters)

  } catch (error) {
  }
}

/**
 * Track button clicks
 * @param {string} button_name - Name/ID of the button
 * @param {string} button_location - Where the button is located (page/section)
 */
export const trackButtonClick = (button_name, button_location = "") => {
  trackEvent("button_click", {
    button_name,
    button_location,
  })
}

/**
 * Track link clicks
 * @param {string} link_url - The URL being clicked
 * @param {string} link_text - The text of the link
 * @param {string} link_location - Where the link is located
 */
export const trackLinkClick = (
  link_url,
  link_text = "",
  link_location = ""
) => {
  trackEvent("link_click", {
    link_url,
    link_text,
    link_location,
  })
}

/**
 * Track form submissions
 * @param {string} form_name - Name/ID of the form
 * @param {string} form_location - Where the form is located
 */
export const trackFormSubmission = (form_name, form_location = "") => {
  trackEvent("form_submit", {
    form_name,
    form_location,
  })
}

/**
 * Track file downloads
 * @param {string} file_name - Name of the downloaded file
 * @param {string} file_url - URL of the downloaded file
 */
export const trackDownload = (file_name, file_url) => {
  trackEvent("file_download", {
    file_name,
    file_url,
  })
}

/**
 * Track user engagement time on page
 * @param {string} page_name - Name of the page
 * @param {number} engagement_time_msec - Time spent on page in milliseconds
 */
export const trackEngagement = (page_name, engagement_time_msec) => {
  trackEvent("user_engagement", {
    page_name,
    engagement_time_msec,
  })
}

/**
 * Track custom conversions/goals
 * @param {string} conversion_name - Name of the conversion
 * @param {object} conversion_data - Additional conversion data
 */
export const trackConversion = (conversion_name, conversion_data = {}) => {
  trackEvent("conversion", {
    conversion_name,
    ...conversion_data,
  })
}

/**
 * Set user properties
 * @param {object} properties - User properties to set
 */
export const setUserProperties = (properties) => {
  try {
    // Google Analytics 4 via gtag
    gtag("config", "G-Z3C1EBWVDS", {
      custom_map: properties,
    })

    // Firebase Analytics - set user properties
    Object.entries(properties).forEach(([key, value]) => {
      analytics.setUserProperties({ [key]: value })
    })

  } catch (error) {
  }
}

/**
 * Track search queries
 * @param {string} search_term - The search term used
 * @param {string} search_location - Where the search was performed
 */
export const trackSearch = (search_term, search_location = "") => {
  trackEvent("search", {
    search_term,
    search_location,
  })
}

/**
 * Track video interactions
 * @param {string} video_title - Title of the video
 * @param {string} action - Action performed (play, pause, complete, etc.)
 * @param {number} video_current_time - Current time in the video
 */
export const trackVideoInteraction = (
  video_title,
  action,
  video_current_time = 0
) => {
  trackEvent("video_" + action, {
    video_title,
    video_current_time,
  })
}
